import plur from 'plur';

/**
 * The "load new" pattern for newest-first feeds (Profile activity, Message activity). A
 * background check reads the first page on its own cache key, so the table does not move while
 * the user reads it. The table then offers the newer entries as a "Load N new …" row.
 */

/** How often the "load new" check reads the first page: every 15s, four times a minute. */
export const NEW_ITEMS_CHECK_INTERVAL_MS = 15_000;

export interface NewItemsCount {
  /** How many entries on the checked page the list does not show. */
  count: number;
  /** True when the API has more new entries past the checked page. */
  hasMore: boolean;
}

/** The noun for the entries, in both forms, e.g. `{ one: 'message', other: 'messages' }`. */
export interface NewItemsNoun {
  one: string;
  other: string;
}

/**
 * How many entries on the latest page the list does not show yet. An entry can land anywhere on
 * the page, not only at the top: a status filter picks up an older delivery, a sent draft joins
 * the Sent list, an event arrives with an older timestamp. So count every unseen id. When none of
 * the page is loaded, `latestHasMore` (the page has a next cursor) says whether more new entries
 * exist past it.
 */
export function countNewItems(
  latestIds: readonly string[],
  loadedIds: ReadonlySet<string>,
  latestHasMore: boolean,
): NewItemsCount {
  const count = latestIds.filter((id) => !loadedIds.has(id)).length;
  const isAllNew = count > 0 && count === latestIds.length;
  return { count, hasMore: isAllNew && latestHasMore };
}

/** "Load 1 new message", "Load 3 new messages", or "Load 50+ new messages" when more exist. */
export function loadNewLabel({ count, hasMore }: NewItemsCount, noun: NewItemsNoun): string {
  return hasMore ? `Load ${count}+ new ${noun.other}` : `Load ${count} new ${plur(noun.one, noun.other, count)}`;
}

/** The screen-reader announcement for new entries: "3 new messages available", or '' for none. */
export function newItemsAnnouncement({ count, hasMore }: NewItemsCount, noun: NewItemsNoun): string {
  if (count === 0) {
    return '';
  }
  return hasMore
    ? `${count}+ new ${noun.other} available`
    : `${count} new ${plur(noun.one, noun.other, count)} available`;
}
